// src/features/organisation/components/roles/RoleManagement.tsx
import React, { useState } from 'react';
import { Shield, CheckCircle, XCircle, Users, Plus, Trash2 } from 'lucide-react';
import { useRoles } from '../../hooks/useRoles';
import { rolesApi } from '../../services/organisationApi';
import { Role } from '../../../../types/organisation/roles.types';
import { TableColumn } from '../../../../types';
import DataTable from '../../../../components/ui/DataTable';
import RoleDetailForm from './RoleDetailForm';
import OrganisationNavigation from '../OrganisationNavigation';

const RoleManagement: React.FC = () => {
  const { roles, loading, refresh } = useRoles();
  const [selectedId, setSelectedId] = useState<number | null | undefined>(undefined);
  const [selectedRows, setSelectedRows] = useState<Role[]>([]);

  const columns: TableColumn<Role>[] = [
    {
      key: 'roleCode',
      title: 'Code',
      sortable: true,
    },
    {
      key: 'roleName',
      title: 'Nom du rôle',
      sortable: true,
    },
    {
      key: 'roleCategory',
      title: 'Catégorie',
      render: (value: unknown) => value?.name || '-',
    },
    {
      key: 'roleScope',
      title: 'Portée',
      render: (value: unknown) => value?.name || '-',
    },
    {
      key: 'isQhseRole',
      title: 'QHSE',
      render: (value: boolean) => value ? (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <Shield className="h-3 w-3 mr-1" />
          QHSE
        </span>
      ) : null,
    },
    {
      key: 'assignmentCount',
      title: 'Attributions',
      render: (value: number) => (
        <span className="text-sm text-gray-600">{value || 0}</span>
      ),
    },
  ];

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) return;
    if (!window.confirm(`Supprimer ${selectedRows.length} rôle(s) ?`)) return;
    try {
      await Promise.all(selectedRows.map(r => rolesApi.delete(r.id)));
      setSelectedRows([]);
      await refresh();
    } catch {
      alert('Erreur lors de la suppression');
    }
  };

  const handleClose = async () => {
    setSelectedId(undefined);
    await refresh();
  };

  return (
    <div className="space-y-6">
      <OrganisationNavigation />

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
        <div className="flex items-center space-x-3">
          <Shield className="h-8 w-8 text-orange-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestion des Rôles</h1>
            <p className="text-gray-600">Définissez les rôles et responsabilités</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {selectedRows.length > 0 && (
            <button
              onClick={handleBulkDelete}
              className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            >
              <Trash2 className="h-4 w-4" />
              <span>Supprimer ({selectedRows.length})</span>
            </button>
          )}
          <button
            onClick={() => setSelectedId(null)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            <span>Nouveau rôle</span>
          </button>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-orange-500 rounded-md p-3">
              <Shield className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">{(roles || []).length}</div>
              <div className="text-sm text-gray-600">Rôles total</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-green-500 rounded-md p-3">
              <CheckCircle className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">
                {(roles || []).filter(r => r.isQhseRole).length}
              </div>
              <div className="text-sm text-gray-600">Rôles QHSE</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-blue-500 rounded-md p-3">
              <Users className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">
                {(roles || []).filter(r => r.isManagerRole).length}
              </div>
              <div className="text-sm text-gray-600">Rôles management</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-red-500 rounded-md p-3">
              <XCircle className="h-6 w-6 text-white" />
            </div>
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">
                {(roles || []).filter(r => !r.isQhseRole).length}
              </div>
              <div className="text-sm text-gray-600">Rôles non-QHSE</div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow border border-gray-200">
        <DataTable
          data={roles || []}
          columns={columns}
          loading={loading}
          selectable={true}
          onSelectionChange={setSelectedRows}
          onRowDoubleClick={(role) => setSelectedId(role.id)}
          emptyMessage="Aucun rôle trouvé"
        />
      </div>

      {selectedId !== undefined && (
        <RoleDetailForm
          roleId={selectedId}
          onClose={handleClose}
          onSave={() => refresh()}
        />
      )}
    </div>
  );
};

export default RoleManagement;
